import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Award, BookOpen, CheckCircle, Compass, Crown, FileCheck, GraduationCap, Lock, Star, TrendingUp, Zap } from 'lucide-react';

const ICON_MAP = {
  'book-open': BookOpen, 'compass': Compass, 'graduation-cap': GraduationCap,
  'award': Award, 'file-check': FileCheck, 'check-circle': CheckCircle,
  'star': Star, 'trending-up': TrendingUp, 'zap': Zap, 'crown': Crown,
};

const UNLOCK_LABELS = {
  es: {
    lesson_count: { plural: 'lecciones', singular: 'leccion' },
    task_count: { plural: 'tareas', singular: 'tarea' },
    correction_count: { plural: 'correcciones', singular: 'correccion' },
    reinforcement_count: { plural: 'actividades de refuerzo', singular: 'actividad de refuerzo' },
  },
  en: {
    lesson_count: { plural: 'lessons', singular: 'lesson' },
    task_count: { plural: 'tasks', singular: 'task' },
    correction_count: { plural: 'corrections', singular: 'correction' },
    reinforcement_count: { plural: 'reinforcement activities', singular: 'reinforcement activity' },
  },
};

export function getUnlockMessage(badge, language) {
  const remaining = badge.remaining ?? 0;
  const labels = UNLOCK_LABELS[language]?.[badge.condition_type];
  if (!labels) return null;
  const noun = remaining === 1 ? labels.singular : labels.plural;
  if (language === 'en') {
    return remaining === 0
      ? `Submit ${badge.condition_value} ${noun} to unlock`
      : `Submit ${remaining} more ${noun} to unlock`;
  }
  return remaining === 0
    ? `Entrega ${badge.condition_value} ${noun} para desbloquear`
    : `Te ${remaining === 1 ? 'falta' : 'faltan'} ${remaining} ${noun} mas para desbloquear`;
}

function BadgeIcon({ badge, isEarned }) {
  const Icon = ICON_MAP[badge.icon] || Award;
  if (isEarned) return <Icon className="w-7 h-7 text-white" />;
  return (
    <div className="relative">
      <Icon className="w-7 h-7 text-slate-400" />
      <Lock className="w-3 h-3 text-slate-500 absolute -bottom-1 -right-1" />
    </div>
  );
}

export default function BadgeTile({ badge, language, fallbackHint }) {
  const name = language === 'en' ? badge.name_en : badge.name_es;
  const desc = language === 'en' ? badge.description_en : badge.description_es;
  const isEarned = !!badge.earned;
  const gradient = isEarned
    ? { background: `linear-gradient(135deg, ${badge.color}, ${badge.color}CC)` }
    : { background: '#E2E8F0' };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-testid={`badge-${badge.id}`}
          className={`relative w-16 h-16 rounded-xl flex items-center justify-center transition-all cursor-pointer ${
            isEarned ? 'shadow-md hover:shadow-lg hover:scale-110' : 'opacity-30 grayscale hover:opacity-50'
          }`}
          style={gradient}
        >
          <BadgeIcon badge={badge} isEarned={isEarned} />
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[220px]">
        <p className="font-bold text-sm">{name}</p>
        <p className="text-xs text-slate-500">{desc}</p>
        {isEarned ? (
          badge.earned_at && (
            <p className="text-[10px] text-emerald-500 mt-1" data-testid={`badge-earned-${badge.id}`}>
              {new Date(badge.earned_at).toLocaleDateString()}
            </p>
          )
        ) : (
          <p className="text-[11px] text-amber-600 font-semibold mt-1.5" data-testid={`badge-locked-${badge.id}`}>
            {getUnlockMessage(badge, language) || fallbackHint}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
